// Upload rules mirrored from Supabase `storage.buckets`.
//
// The limits are enforced by Storage itself, which rejects a bad object with a
// bare 400 and no body — from the app that is indistinguishable from a broken
// upload. Checking here means the agent is told which file is wrong and why,
// before a large photo is pushed over a mobile connection for nothing.
//
// KEEP IN SYNC with the bucket definitions in
// cid_database/supabase/config.toml and the live storage.buckets rows.
export const UPLOAD_RULES = {
  'listing-images': {
    maxBytes: 5 * 1024 * 1024,
    types: ['image/jpeg', 'image/png', 'image/webp'],
    label: 'JPG, PNG or WebP image',
  },
  // Any document type; only programs and web pages are refused. The bucket itself
  // has no type list, so this check is the rule, not a mirror of one.
  'agent-documents': {
    maxBytes: 25 * 1024 * 1024,
    types: [],
    blockedExt: ['exe', 'msi', 'bat', 'cmd', 'com', 'scr', 'ps1', 'vbs', 'js', 'mjs', 'jar', 'sh', 'apk', 'app', 'dmg', 'html', 'htm', 'xhtml', 'svg', 'php'],
  },
};

const asMB = (bytes) => `${(bytes / (1024 * 1024)).toFixed(1)} MB`;

/**
 * @returns {string|null} a message to show the agent, or null when the file is fine.
 */
export function checkUpload(file, bucket = 'listing-images') {
  const rule = UPLOAD_RULES[bucket];
  if (!rule || !file) return null;

  if (rule.types.length && !rule.types.includes(file.type)) {
    return `“${file.name}” isn’t a supported format — use a ${rule.label}.`;
  }
  const ext = file.name?.includes('.') ? file.name.split('.').pop().toLowerCase() : '';
  if (rule.blockedExt?.includes(ext)) {
    return `“${file.name}” can’t be uploaded — .${ext} files aren’t allowed.`;
  }
  if (file.size > rule.maxBytes) {
    return `“${file.name}” is ${asMB(file.size)}. The limit is ${asMB(rule.maxBytes)} — please make it smaller and try again.`;
  }
  return null;
}

/** Split a picked file list into what can be uploaded and why the rest cannot. */
export function partitionUploads(files, bucket = 'listing-images') {
  const ok = [];
  const rejected = [];
  for (const file of files) {
    const problem = checkUpload(file, bucket);
    if (problem) rejected.push(problem); else ok.push(file);
  }
  return { ok, rejected };
}
